<template>
  <div v-once>
    <el-row :gutter="20" style="font-family: Oxygen; font-size: 1em;">

      <el-col id="help-content" style="padding-left: 20px; padding-right: 20px; padding-top: 40px" :lg='{"offset": 1, "span": 22}' align="left">

        <el-collapse id="help-collapse" accordion>
          <el-collapse-item name="1">
            <template slot="title">
              <h3>Layout</h3>
            </template>
            <div>
              The new version of MSDB is split into four major functional pages.
              <ul style="line-height: 1.4em">
                <li><b>Home</b> - Introduction page summarising the stats and features of MSDB.</li>
                <li><b>Browse</b> - The main page on which the user can explore, analyse, and visualise the
                  microsatellite data.</li>
                <li><b>Download</b> - A page dedicated to quickly download the microsatellite data of available species.
                </li>
                <li><b>Help</b> - A manual page for complete usage and understanding of MSDB.</li>
              </ul>
            </div>
          </el-collapse-item>

          <el-collapse-item name="2">
            <template slot="title">
              <h3>Browse page</h3>
            </template>
            <div>
              <el-row :gutter="20">
                <el-col :lg="8" :md="9" :sm="24" :xs="24">
                  <p>
                    Browse page adopts a modern dashboard style user experience. By default, the microsatellite data of
                    <i>Homo sapiens</i> (build hg38 from UCSC) is loaded. </p>
                  <p>
                    The page is split into two sections. To the left there is species selection and operations panel, and
                    the remaining area displays the microsatellite data of the selected species.</p>
                  <p>The plots on the dashboard give a snapshot of the microsatellite profile for the selected species.
                    All the plots, tables, and their features are explained further in sections below
                  </p>
                </el-col>
                <el-col :lg="15" :md="15" :sm="24" :xs="24">
                  <img style="width: 100%; height: auto;" src="../../public/data/help1_msdblayout.png">
                </el-col>
              </el-row>
            </div>
          </el-collapse-item>

          <el-collapse-item name="3">
            <template slot="title">
              <h3>Dashboard components</h3>
            </template>
            <div>

              <div>
                <h3 style="padding-top: 10px">Current species</h3>
                <el-row :gutter="20">
                  <el-col :span="4"><img style="width: 100%; height: auto"
                      src="../../public/data/help3_active_species.png"></el-col>
                  <el-col :span="20">
                    <p>
                      In the species selection panel the active species is highlighted as shown for <b>Homo Sapiens</b>
                      in the example here.
                      The dashboard now gets updated to display the information of this active species.</p>
                    <p>
                      Users can click on any selected species to change the active species. Clicking the <el-button
                        size="mini" :plain="true">X</el-button> button will remove the species from the selected list.
                    </p>
                  </el-col>
                </el-row>
              </div>

              <el-row :gutter="20" style="margin-top: 30px">
                <el-col :lg="9" :md="9" :xs="24" :sm="24">
                  <h3>Basic info</h3>
                  <p>
                    The header of the dashboard has the name and the heirarchical taxonomic information of the species.
                    Immediately after the header are a few stats which introduce the microsatellite profile to the
                    user.<br>
                    <br>
                    On the top right of the dashboard are three buttons which have specific actions.
                    The first button,
                    <el-button style="cursor: default" icon="el-icon-info" plain type="primary"></el-button> , when
                    hovered displays a pop-down
                    containing genomic information for the species. <br><br>
                    The download button, <el-button style="margin-left: 10px; cursor: default" plain type="primary"
                      icon="el-icon-download"></el-button> ,
                    allows the user to directly download the microsatellite data of the species in a tabular format (not
                    gzip compressed).<br><br>
                    The third button,
                    <el-button style="cursor: default" type="primary">Explore Repeats</el-button> opens the
                    microsatellite data of the species as
                    a table in a new webpage.
                  </p>
                </el-col>
                <el-col :lg="14" :md="14" :xs="24" :sm="24"><img style="width: 100%; height: auto"
                    src="../../public/data/help4_dashboard_actions.png"></el-col>
              </el-row>


              <el-row :gutter="20" style="margin-top: 30px">
                <el-col :span="23">
                  <h3>Plots and Tables</h3>
                  <p>
                    Species dashboard has 3 major plots for every genome and 3 additional plot for genomes with
                    available gene annotations (The status of gene annotation for a given genome is shown in the genome
                    card explained above).
                    There are also two tables, one which reports the occurences of the longest repeats and second which
                    reports the
                    repeat class wise information of genomic frequency and frequency in different annotated regions (if
                    annotations are available).
                    <br><br>
                    The plots are explained in detail below. Most dashboard version plots have a button
                    <el-tooltip content="Explore more" placement="top" :enterable="false">
                      <el-button circle icon="el-icon-top-right" size="large"></el-button>
                    </el-tooltip>
                    beside the header of the plot, which when clicked opens up a modal interactive controls to change
                    what is plotted. This is one of the most unique features of MSDB, which allows the user to not only
                    the displayed data for the active species, but to plot multiple charts for all selected species side
                    by side.
                    This is explained in more detail in the modal layout section.
                  </p>
                </el-col>
              </el-row>
              <el-row>
                <el-col :span="23" align="center" style="margin-top: 20px; amrgin-bottom: 15px">
                  <img style="width: 80%; height: auto" src="../../public/data/help5_dashboard_firstrow.png">
                </el-col>
              </el-row>
              <el-row>
                <div>
                  <ol>
                    <b>The image above shows the first row of dashboard components.</b><br><br>
                    <li style="font-weight: bold; font-size: 20px">Motif size distribution</li>
                    <p>
                      This plot describes the composition of repeats based on the motif size.
                      The slices in the donut chart represent the percentage of all repeats that belong to a specific
                      motif size.
                      Microsatellite motif sizes range from 1 nt (Monomers) to 6 nt (Hexamers).
                      <!-- Of all the 501 repeat classes the number of unique hexamers are highest and hence we expect to see a highest percentage of
                        hexamers in the genome (in general) followed by pentamers and so on. But who knows in which species we might see a shift
                        in this expected pattern.  -->
                    </p>
                    <li style="font-weight: bold; font-size: 20px">Most frequent repeats</li>
                    <p>
                      This bar chart is designed to visualise the frequency of all the repeat classes in the genome.
                      By default, the dashboard version shows top 10 most frequent repeats in the genome. The user can
                      explore more by going into the modal view where the user can view the frequency of desired repeat
                      classes
                      or even sort to see the data in ascending or descending order.
                    </p>
                    <li style="font-weight: bold; font-size: 20px">Longest repeats in the genome</li>
                    <p>
                      This is a table which gives the information of the 10 longest repeats in the genome.
                      The table has information on location of the repeat, length of repeat sequence and class of the
                      repeat.
                    </p>
                    <el-col :span="24" align="center" style="margin-top: 10px; margin-bottom: 10px">
                      <img style="width: 80%; height: auto" src="../../public/data/help6_dashboard_secondrow.png">
                    </el-col>
                    <b>The image above shows the second row of dashboard components.</b><br><br>
                    <li style="font-weight: bold; font-size: 20px">Repeat class informatiion</li>
                    <p>
                      This table lists out repeat class wise information of frequency and bases covered in the genome.
                      If the annotation is
                      available for a genome the table gives further information of percentage of the repeat class
                      occurences in exonic, intronic,
                      intergenic and promoter regions (we defined promoters as -1kb to +1kb of TSS). All of the columns
                      in the table are sortable both in ascending and descending order. The table is sortable, and hence
                      can help retrieve interesting information, for example repeat classes which have highest occurence
                      in exonic regions.
                    </p>
                    <li style="font-weight: bold; font-size: 20px">Length vs Frequency</li>
                    <p>
                      Length vs frequency plot shows the user the number of occurences of a selected repeat class at
                      different repeat lengths.
                      The dashboard version of plot shows information for the top 5 abundant repeats in the genome.
                      <br><br>
                      <el-row :gutter="20">
                        <el-col :span="8">
                          <img style="width: 100%; height: auto; border-radius: 10px; border: 1px dashed black"
                            src="../../public/data/help8_ramamoorthy_paper.png">
                        </el-col>
                        <el-col :span="16">
                          <a href="https://www.sciencedirect.com/science/article/pii/S0378111914009913?via%3Dihub"
                            target="_blank"><i>S. Ramamoorthy et al. / Gene 551 (2014) 167–175</i></a>
                          have shown that few repeats in the human genome and other eukaryotic organisms show a length
                          enrichment pattern. This pattern explains that few repeats have been conserved at this
                          specific length, hinting towards positive selection and thus a possible functional
                          significance. The group has also identified the functional roles of these microsatellites at
                          the enriched lengths in a later study
                          <a href="https://www.sciencedirect.com/science/article/pii/S0378111917305334?via%3Dihub"><i>J.
                              Krishnan et al. / Gene 628 (2017) 156–161</i></a>
                        </el-col>
                      </el-row>
                    </p>

                    <el-col :span="24" align="center" style="margin-top: 10px; margin-bottom: 10px">
                      <img style="width: 80%; height: auto" src="../../public/data/help7_dashboard_annotation.png">
                    </el-col>
                    <b>The image above shows the third row of dashboard components. <br>
                      This row displays microsatellite information in context of genomic annotations, and hence will
                      only be visible for genomes which have annotations available.</b><br><br>
                    <li style="font-weight: bold; font-size: 20px">Genomic distribution of repeats</li>
                    <p>
                      The first plot in this row gives an overview of genomic location of all repeats. It plots the
                      percentage of repeats which fall in different genomic
                      regions, such as (Exnoic, Intronic, Intergenic)
                    </p>
                    <li style="font-weight: bold; font-size: 20px">Gene centric distribution of repeats</li>
                    <p>
                      This second plot shows the frequency of repeat classes around TSS of genes.
                    </p>
                    <li style="font-weight: bold; font-size: 20px">Kmer distribution in Genomic regions</li>
                    <p>
                      This stacked bar plot shows the composition of repeats in different annotation categories.
                    </p>
                  </ol>
                </div>
              </el-row>

            </div>
          </el-collapse-item>

          <el-collapse-item name="4">
            <template slot="title">
              <h3>Species selection</h3>
            </template>
            <div>
              <el-row style="margin-top: 30px">
                <el-col :span="18" :offset="2">
                  <img style="width: 100%; border-radius: 5px; height: auto"
                    src="../../public/data/help2_species_selection.png">
                </el-col>
              </el-row>

              <el-row>
                <el-col :lg="23" :md="23" :sm="24" :xs="24">
                  <p>
                    Species selection can be done in two ways.
                    <ol>
                      <li style="color: red">
                        <b>Species table</b>
                        <p style="color: black">
                          The species selection panel in the browse page has <el-button type="success"
                            style="cursor: default">Open Species Table</el-button> button which when clicked opens a modal
                          showing the all species in a tabular format.<br>
                          <br>
                          The table has all the genomes present in MSDB. Each genome is given an MSDBID which has a format
                          of <b>MSDBG######</b>. For each organism we have added information, such as the taxonomic
                          classification of the species (adopted from NCBI's Genome Browser) and other genomic
                          information. We believe that this auxiliary data on each genome helps the user to make an
                          informed selection, particularly when multiple builds/versions of the genomes are available for
                          a species.<br>
                          <br>
                          Above the table on the left side is a search bar
                          <el-input style="width: 200px" placeholder="Search species by name..."></el-input>
                          <el-button type="primary" icon="el-icon-search" style="cursor: default"></el-button>
                          which lets the user search for a species by name (common names are supported too, where
                          available). On the right side above the table are options
                          <el-select value="" style="margin-right: 5px">
                            <el-option label="Selection" value="selected"></el-option>
                            <el-option value="dummy">MSDBID</el-option>
                            <el-option value="dummy">Genome Size</el-option>
                            <el-option value="dummy">GC%</el-option>
                            <el-option value="dummy">Build</el-option>
                          </el-select>
                          <el-checkbox label="Ascending"></el-checkbox>
                          to sort the table on any of the columns.<br>
                          <br>
                          The last column in the table has two action buttons where clicking on <el-button type="success"
                            icon="el-icon-check" size="large" clicked circle plain style="cursor: default"></el-button>
                          adds the species to the selected species list and clicking <el-button type="danger"
                            icon="el-icon-close" size="large" circle plain style="cursor: default"></el-button> wil remove
                          it.
                        </p>
                        <p style="color: black">
                          All the species that are currently selected are shown in the left panel. The total number of
                          selected species are shown in parentheses. Clicking on the species name in the panel loads the
                          microsatellite data for that species in the dashboard area. Clicking the <el-button size="mini"
                            :plain="true">X</el-button> button will remove the species from the selected list.
                        </p>
                      </li>
                      <li style="color: blue">
                        <b>Searching species by name</b>
                        <p style="color: black">
                          The second way of selecting a species is an immediate serach option integrated in the species
                          selection panel. User can search for any species with either the species name or the common name
                          of the species. (Ex. Dog for <i>Canis lupus</i>). The suggestions get activated after the user
                          types at least 3 characters.
                        </p>
                      </li>
                    </ol>
                  </p>
                </el-col>
              </el-row>
            </div>
          </el-collapse-item>          

          <el-collapse-item name="5">
            <template slot="title">
              <h3>Modal layout</h3>
            </template>
            <div>
              <el-row :gutter="20">
                <el-col :lg="23">
                  <h3>Single organism plot</h3>
                  <img style="width: 100%; height: auto" src="../../public/data/help16_modalview.png">
                  <p>
                    Every plotcard on the dashboard has a
                    <span>
                      <el-tooltip content="Explore more" placement="top" :enterable="false">
                        <el-button circle size="large"><i class="fas fa-icon-expand"></i></el-button>
                      </el-tooltip>
                    </span> in the header,
                    which opens a modal and lets the user explore the plot in more detail.
                    The modal opens up like in the image shown above. By default the modal shows the plot of the current
                    selected
                    organism as indicated in the navigation pane in the left of the modal. The user can select species
                    from
                    the pane and explore the plot for the corresponding organism. Each plot has appropriate plot controls
                    which lets the
                    user manipulate the data that is being plotted. For example in the above case the user is looking at
                    the most abundant
                    repeat classes in the human genome. This plot has controls to select for how many repeat classes
                    should the data be shown
                    and also to select specific repeats for which abundance should be plotted.
                  </p>
                </el-col>
              </el-row>
              <el-row>
                <h3>Multi-species comaprison</h3>
                <el-col :lg="15" :md="15" :sm="24" :xs="24">
                  <img style="width: 100%; height: auto" src="../../public/data/help15_modal_multispecies.png">
                </el-col>
                <el-col :lg="8" :md="8" :sm="24" :xs="24">
                  <p>
                    The user can opt to plot all the selected organisms by switching to all selected organisms
                    <el-radio-group v-model="alldefault" size="small">
                      <el-tooltip class="item" content="Plot data for the current organism" effect="dark" placement="top"
                        :enterable="false">
                        <el-radio-button label="current">Current Organism</el-radio-button>
                      </el-tooltip>
                      <el-tooltip class="item" content="Compare data for all selected organisms" effect="dark"
                        placement="top" :enterable="false">
                        <el-radio-button checked="true" label="all">All selected Organisms</el-radio-button>
                      </el-tooltip>
                    </el-radio-group>.
                    By default the data for all selected organisms is plotted. The plot controls corresponding to the
                    single oragnism
                    anlysis are also operative here. Plot controls specific to multi-species analysis are made available.
                    These controls
                    mainly include options to normalise the data across species for a better comparison.<br> Also as shown in
                    the image there is
                    an option which common to all the plots i.e.,
                    <span>
                      <div class="option-label" style="padding-bottom: 5px; padding-top: 10px; display: inline">  &#8195; Group by &#8195;  </div>
                      <el-tooltip class="item" content="Group data by repeats or by organism" effect="dark"
                        placement="bottom" :enterable="false">
                        <el-switch style="padding-top: 5px" v-model="groupByDefault" inactive-color="#13ce66"
                          active-color="#13ce66" active-text="Repeat" inactive-text="Organism"></el-switch>
                      </el-tooltip> &#8195;
                    </span>. The plots are by default grouped by organism, meaning data pertaining to single organism is shown in one plot. While when is switched to group by Repeat, data for single repeat class from all selected species is grouped and shown in one plot. This grouping let's the user compare trends of each repeat class in the selected species much more conveniently and accurately.
                  </p>
                </el-col>
              </el-row>
            </div>
          </el-collapse-item>

          <el-collapse-item name="6">
            <template slot="title">
              <h3>Repeats data table</h3>
            </template>
            <div>
              <el-row :gutter="20">
                <el-col :lg="15" :md="15" :sm="24" :xs="24">
                  <img style="width: 100%; height: auto" src="../../public/data/help17_repeats_table.png">
                </el-col>
                <el-col :lg="8" :md="9" :sm="24" :xs="24">
                  <p>
                    The <el-button style="cursor: default" type="primary">Explore Repeats</el-button> button from the dashboard directs the user to the repeats data table page.
                    The table reports all the repeat instances in the selected genome and their genomic context. The page has a simple layout with the table at the bottom, options to navigate through the table just above it and a dedicated filter panel at the top of the page. The table is sortable on all the columns.<br>
                    The filter panel houses options to filter out only those repeat instances matching a desired set of criteria. The usage of the options are intuitive and staright
                    forward. In the example image chromosome search filter is applied to show repeat instances of from chromosome 1. The yellow arrow in the image points to the stat which displays the number of repeats which passed the filter of desired criteria out of the total repeats. <el-button style="cursor: default" type="danger">Clear Filters</el-button> button removes all the filters and displays the original table with all repeat instances.<br>
                    The <el-button style="cursor: default" type="primary">Export file</el-button> button downloads the actively dispayed table as a tsv file.
                    The button, <el-button style="cursor: default" type="primary">Get sequences</el-button> opens up a new page showing sequences for the repeat regions selected in table.
                  </p>
                </el-col>
              </el-row>
            </div>
          </el-collapse-item>

          <el-collapse-item name="7">
            <template slot="title">
              <h3>Download page</h3>
            </template>
            <div>
              <el-row :gutter="20">
                <el-col :lg="15" :md="15" :sm="24" :xs="24">
                  <img style="width: 100%; height: auto" src="../../public/data/help12_downlaod_page.png">
                </el-col>
                <el-col :lg="8" :md="9" :sm="24" :xs="24">
                  <p>
                    The Download page displays all the genomes as a table with options to download the microsatellite data
                    using the links in the last column.
                    <ul style="line-height:25px;">
                      <li> First link, <a>"View Repeats"</a> opens a new tab with microsatellite data for that species
                        displayed as a table for the user to explore. </li>
                      <li> Second link, <a>"Download .tsv"</a> downloads the entire microsatellite data for the species in
                        a tab separated value format file. </li>
                      <li> Third link, <a>"Download .tsv.gz"</a> is same as above, but downloads a gzip compressed file.
                      </li>
                    </ul>
                  </p>
                </el-col>
              </el-row>
            </div>
          </el-collapse-item>

          <el-collapse-item name="8">
            <template slot="title">
              <h3>Data curation</h3>
            </template>
            <div>
              <el-row>
                <el-col :span="23">
                  <p>
                    The genomes for MSDB were curated from two sources - <b>UCSC</b> and <b>NCBI</b>. <br>
                    <ul>
                      <li>
                        From UCSC, we have downloaded the data for the list of
                        <a href="http://hgdownload.soe.ucsc.edu/goldenPath/currentGenomes/" target="_blank">current
                          genomes</a>. We have also downloaded older versions
                        of model organisms which are often used in genomic studies. <br> <br>
                      </li>
                      <li>
                        We have a more automated procedure to procure genomes from NCBI.
                        From NCBI's <a href="https://www.ncbi.nlm.nih.gov/genome/browse#!/overview/"
                          target="_blank">genome browser</a>
                        we have taken the <a href="https://ftp.ncbi.nlm.nih.gov/genomes/GENOME_REPORTS/overview.txt"
                          target="_blank">overview</a>
                        table of all species. Excluding all the virus genomes we have selected a unique list of
                        eukaryotic, archaea, and prokaryotic species.
                        This list of unique species is searched for in RefSeq and GenBank list of genomes. For species
                        which had multiple assemblies available,
                        we have taken the ones with the superior assembly level (Chromosomes > Scaffolds > Contigs),
                        highest contig N50 value, and also the latest by release date. For species which had
                        RefSeq assemblies available we have not downloaded the GenBank version as RefSeq versions are
                        better curated.
                        <br>
                        We have seen that some species in the NCBI's list of <a
                          href="https://ftp.ncbi.nlm.nih.gov/genomes/GENOME_REPORTS/eukaryotes.txt"
                          target="_blank">eukaryotic species</a>
                        are missing in the <a href="https://ftp.ncbi.nlm.nih.gov/genomes/GENOME_REPORTS/overview.txt"
                          target="_blank">overview</a> list. These missed species are queried separately in the
                        RefSeq and Genbank databases and downloaded after a manual selection process.
                      </li>
                    </ul>
                    <i>The genome data is updated as of May 21st, 2019 in the current version of MSDB.</i>
                  </p>
                </el-col>

              </el-row>
            </div>
          </el-collapse-item>

          <el-collapse-item name="9">
            <template slot="title">
              <h3>Identification of repeats</h3>
            </template>
            <div>
              <el-row :gutter="20">
                <el-col :span="23">
                  <p>
                    The identification of microsatellites for each genome is done using <a
                      href="https://github.com/RKMlab/perf">PERF</a>, developed by us.
                    PERF exhaustively identifies repeats in genomes and hence is 100% accurate. We have set the
                    identifiation parameters to 1-6bp motfif sizes and
                    minimum length cutoff as 12bp. PERF outputs all the identified locations in a tabular format that
                    follows the BED specification, with each repeat instance in a row. The same table could be viewed in
                    MSDB by clicking the <el-button style="cursor: default" type="primary">Explore Repeats</el-button>
                    button. For each repeat instance, it's chromosome coordinates, repeat class (explained below),
                    length
                    of repeat, units, orientation and actual motif are reported.
                    <br>
                    <div style="padding-left: 20px">
                      <h3>Repeat annotation</h3>

                      We have added an annotation module to PERF (yet to become public) which annotates the repeat
                      locations given a genome annotation file
                      (GFF or GTF format).<br>Each repeat instance is given two annotation labels:
                      <ul>
                        <li>Genomic annotation - specifies if the repeat is falls in Exonic or Intronic, Intergenic
                          regions.</li>
                        <li>
                          Promoter annotation - specifies if the repeat falls in promoter region of any gene. We have
                          considered 1000bp upstream
                          and downstream of TSS as promoter region of the gene. This annotation is only used for the
                          plots in Browse page, and is hidden in the table view. Instead, it is recommended to use the
                          distance to closest TSS, which is also reported for each repeat. This allows a flexibility for
                          users to define their own promoter criteria.
                        </li>
                      </ul>
                    </div>
                  </p>
                </el-col>
              </el-row>
              <el-row style="padding-left: 30px" :gutter="20">
                <h3>Explanation of repeat classes</h3>
                <el-col :lg="11" :md="11" :sm="24" :xs="24">
                  <p>
                    A total of 5356 permutations of 1 to 6nt are possible. These are categorized into 501 unique repeat
                    classes based on the cyclical
                    variations of the motif and its reverse complement. All cyclical variations of the repeat motif are
                    considered to be on the '+' strand whereas
                    those of its reverse complement are considered to be on the '-' strand. In case the repeat motif is
                    a palindrome or a cyclical variation of a
                    palindrome, all the cyclical variations are considered to be on the '+' strand. This is better
                    explained in the table below:
                  </p>
                  <el-table stripe border :data="tableData" style="width: 100%">
                    <el-table-column prop="repeatClass" label="Repeat class"> </el-table-column>
                    <el-table-column prop="cvPositive" label="Cyclical Variations ('+' strand)"> </el-table-column>
                    <el-table-column prop="cvNegative" label="Reverse Complement ('-' strand)	N"> </el-table-column>
                    <el-table-column prop="nMotifs" label="Number of motifs in class"> </el-table-column>
                  </el-table>
                </el-col>
                <el-col :lg="12" :md="12" :sm="24" :xs="24">
                  <img style="width: 100%; height: auto" src="../../public/data/help11_repeatclass.jpg">
                  <p>
                    <i>
                      In this example, the actual repeat is ATAG, as determined by the first 4 bases of the entire
                      repeat sequence.
                      However, this repeat falls into the AGAT class.
                    </i>
                  </p>
                </el-col>
              </el-row>
            </div>
          </el-collapse-item>

          <el-collapse-item name="10">
            <template slot="title">
              <h3>Contact</h3>
            </template>
            <div>
              <p>
                For queries or suggestions please contact:
                <ul>
                  <li>Divya Tej Sowpati - <a href="mailto: tej@ccmb.res.in">tej@ccmb.res.in</a></li>
                  <li>Akshay Avvaru - <a href="mailto: avvaru@ccmb.res.in">avvaru@ccmb.res.in</a></li>
                  <li>Rakesh K Mishra - <a href="mailto: mishra@ccmb.res.in">mishra@ccmb.res.in</a></li>
                </ul>
              </p>
            </div>
          </el-collapse-item>
        </el-collapse>
      </el-col>
      
    </el-row>
    <div style="color: white; hieght: 100px; background-color: #324157; padding: 20px; margin-top: 60vh">
      <div style="padding: 3px"> Copyright 2019 © CCMB, Uppal Road, Hyderabad - 500007, Telangana, India</div>
      <div style="padding: 3px"> MSDB V3 </div>
      <div style="padding: 3px"> Last updated 3rd September 2019 </div>
    </div>
  </div>

</template>

<script>
  export default {
    name: 'Help',
    data() {
      return {
        groupByDefault: true,
        alldefault: 'all',
        tableData: [{
            repeatClass: 'ACT',
            cvPositive: 'ACT, CTA, TAC',
            cvNegative: 'AGT, GTA, TAG',
            nMotifs: '6'
          },
          {
            repeatClass: 'ACGT',
            cvPositive: 'ACGT, CGTA, GTAC, TACG',
            cvNegative: 'ACGT, CGTA, GTAC, TACG',
            nMotifs: '4'
          },
          {
            repeatClass: 'AATTCG',
            cvPositive: 'AATTCG, ATTCGA, TTCGAA, TCGAAT, CGAATT, GAATTC',
            cvNegative: 'CGAATT, GAATTC, AATTCG, ATTCGA, TTCGAA, TCGAAT',
            nMotifs: '6'
          },
        ]
      }
    },
    methods: {},
  }
</script>

<style>
  p {
    line-height: 1.4em ;
    text-align: justify;
    /* font-size: 1em; */
    /* font-weight: 'normal'; */
  }

  .el-menu-item {
    color: black;
  }

  .el-menu-item.is-active {
    background-color: #324157 !important;
    color: white
  }

  .el-menu-item span {
    font-weight: bold;
    font-size: 1em
  }

  .nav-link {
    text-decoration: none;
  }

  #help-collapse > .el-collapse-item > div > div[role="button"] {
  /* border-width: 1px;
  border-style: solid;
  border-color: #cccccc;
  border-radius: 10px; */
  font-weight: bold;
  font-size: 1em;
  background-color: #EEF1F6;
  padding-left: 15px;
  border-radius: 5px;
  border: 1px solid #B3CDE0;
}

#help-collapse {
  border: none !important;
  border-radius: 10px;
}

#help-collapse > .el-collapse-item {
  padding-top: 20px;
}

#help-collapse > .el-collapse-item > .el-collapse-item__wrap > .el-collapse-item__content {
  padding: 20px;
  text-align: justify;
  font-size: 1em;;

}
</style>